import axios, { type AxiosResponse } from 'axios';
import type { IContactResponse } from '../lib/contact';
import.meta.env.DEV;
import.meta.env.PROD;
// CORS proxies optimized for Russia and international access
const CORS_PROXIES = [
  'https://api.allorigins.win/raw?url=', // Most reliable for Russia
  'https://corsproxy.org/?',
  'https://proxy.cors.sh/',
  'https://corsproxy.io/?',
  'https://cors-anywhere.herokuapp.com/', // Backup option
];

export const createCorsAwareRequest = async (
  url: string
): Promise<AxiosResponse> => {
  console.log(`🌐 Attempting to fetch: ${url}`);
  console.log(`🔄 Environment: ${process.env.NODE_ENV || 'development'}`);

  // In development, use Vite proxy if available
  if (
    process.env.NODE_ENV === 'development' &&
    url.includes('api.krasota.agency')
  ) {
    const proxyUrl = url.replace('https://api.krasota.agency', '/api/krasota');
    console.log(`🔧 Using Vite proxy: ${proxyUrl}`);

    try {
      const response = await axios.get(proxyUrl, {
        timeout: 10000,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
      console.log('✅ Vite proxy request successful');
      return response;
    } catch (error: any) {
      console.warn(
        '❌ Vite proxy failed, falling back to CORS proxies:',
        error.message
      );
    }
  }

  // Try multiple CORS proxies for better reliability
  for (let i = 0; i < CORS_PROXIES.length; i++) {
    const proxy = CORS_PROXIES[i];
    try {
      const requestUrl = proxy + encodeURIComponent(url);
      const requestType = `CORS proxy: ${proxy}`;

      console.log(`📡 Trying ${requestType} (${i + 1}/${CORS_PROXIES.length})`);

      const response = await axios.get(requestUrl, {
        timeout: 8000, // 8 second timeout per attempt
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      // Handle different response formats
      let data = response.data;

      // If response is a string, try to parse it
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (parseError) {
          console.warn('Failed to parse response as JSON:', parseError);
        }
      }

      // Create a normalized response
      const normalizedResponse = {
        ...response,
        data: data,
      };

      console.log(`✅ ${requestType} succeeded`);
      return normalizedResponse;
    } catch (error: any) {
      const errorMessage = error.message || 'Unknown error';
      console.error(`❌ CORS proxy ${i + 1} failed:`, errorMessage);

      // Log specific SSL errors for debugging
      if (
        errorMessage.includes('SSL') ||
        errorMessage.includes('certificate') ||
        errorMessage.includes('ERR_SSL_UNRECOGNIZED_NAME_ALERT')
      ) {
        console.warn(
          '🔒 SSL certificate issue detected with api.krasota.agency'
        );
      }

      // If this was the last proxy, throw the error
      if (i === CORS_PROXIES.length - 1) {
        console.error('❌ All CORS proxies failed');
        throw new Error(
          `All CORS proxy requests failed. The API server (api.krasota.agency) appears to have SSL certificate issues. Last error: ${errorMessage}`
        );
      }
    }
  }

  // This should never be reached due to the throw above, but TypeScript needs it
  throw new Error('All CORS proxy requests failed');
};

export const http = axios.create({
  baseURL: '/api/krasota', // 👈 ALWAYS THIS
  timeout: 10000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
  withCredentials: false,
});
export async function apiRequest<T>(url: string) {
  const response = await http.get<T>(url);
  return response.data;
}
export async function apiPostRequest<T>(
  url: string,
  data?: IContactResponse
): Promise<T> {
  const response = await http.post<T>(url, data);
  return response.data;
}
